export type ItemType = 'url' | 'text' | 'image' | 'file' | 'email';
export type EnrichmentStatus = 'pending' | 'complete' | 'failed';
export type Visibility = 'private' | 'shared';
export type FolderRole = 'owner' | 'editor' | 'viewer';

export interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string;
  createdAt: Date;
}

export interface Item {
  id: string;
  type: ItemType;
  title: string;
  content: string;
  mimeType?: string;
  fileName?: string;
  fileSize?: number;
  articleBody?: string;
  articleHtml?: string;
  emailFrom?: string;
  emailFromName?: string;
  summary: string;
  imageUrl: string;
  tags: string[];
  autoTags: string[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  folderIds: string[];
  visibility: Visibility;
  sharedWith: string[];
  sharedWithContactIds: (string | null)[];
  enrichmentStatus: EnrichmentStatus;
  site?: string;
  flags: number;
}

export const ItemFlags = { STARRED: 1, READ: 2 } as const;
export const hasFlag = (flags: number, flag: number): boolean => (flags & flag) !== 0;

export interface Folder {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  createdBy: string;
  members: Record<string, FolderRole>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Comment {
  id: string;
  text: string;
  createdBy: string;
  createdAt: Date;
}

export type BotTrigger = 'manual' | 'on_item_add' | 'schedule';
export type BotRunStatus = 'running' | 'completed' | 'failed';

export interface Bot {
  id: string;
  name: string;
  description: string;
  instructions: string;
  seq: number;
  triggerType: BotTrigger;
  schedule: string | null;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  lastRunAt: Date | null;
}

export interface BotRun {
  id: string;
  botId: string;
  status: BotRunStatus;
  result: string | null;
  triggeredBy: string;
  createdAt: Date;
  completedAt: Date | null;
}

export interface ContactGroup {
  id: string;
  name: string;
  description: string;
  contactIds: string[];
  memberCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Contact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  nicknames: string[];
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
  flags: number;
  createdAt: Date;
  updatedAt: Date;
}

export const ContactFlags = { STARRED: 1 } as const;

export type EmailDirection = 'inbound' | 'outbound';

export interface Email {
  id: string;
  direction: EmailDirection;
  fromEmail: string;
  fromName: string | null;
  toEmail: string;
  cc: string | null;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  isRead: boolean;
  isStarred: boolean;
  itemId: string | null;
  createdAt: string;
  updatedAt: string;
  item: {
    title: string | null;
    type: string | null;
    imageUrl: string | null;
  } | null;
}

export interface UserSettings {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  inboundHandle: string | null;
  inboundHandle2: string | null;
}

export interface DocumentFolder {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShareFolder {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ContactFolder {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface EmailFolder {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export const DocFlags = { STARRED: 1 } as const;

export interface StashDocument {
  id: string;
  name: string;
  mimeType: string | null;
  fileSize: number | null;
  r2Key: string;
  folderId: string | null;
  source: 'email' | 'upload';
  sourceItemId: string | null;
  flags: number;
  createdAt: string;
  updatedAt: string;
}
